import Vue from 'vue'
import store from 'store'
import { initRedesignCardSliders } from './product-card-slider'

const VIEW_STORAGE_KEYS = {
  desktop: 'collectionViewDesktop',
  tablet: 'collectionViewTablet',
  mobile: 'collectionViewMobile'
}

const VIEW_OPTIONS = {
  desktop: ['three', 'four', 'six'],
  tablet: ['three', 'four'],
  mobile: ['one', 'two']
}

export default (el) => Promise.resolve(new Vue({
  el,
  store,
  data () {
    return {
      drawerOpen: false,
      isLoading: false,
      previewRequestController: null,
      applyStateRequestId: 0,
      currentViews: {
        desktop: 'four',
        tablet: 'four',
        mobile: 'two'
      },
      requestController: null
    }
  },
  created () {
    this.onPopState = () => {
      this.handlePopState()
    }

    this.onKeyDown = (event) => {
      this.handleKeyDown(event)
    }

    window.addEventListener('popstate', this.onPopState)
    window.addEventListener('keydown', this.onKeyDown)
  },
  mounted () {
    this.syncQueryParamsFromUrl()
    this.syncViewsFromDom()
    this.bindDomEvents()

    this.$nextTick(() => {
      initRedesignCardSliders(this.$el)
    })

    if (this.hasStatefulQueryParams()) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToProductGrid()
        }, 500)
      })
    }
  },
  beforeDestroy () {
    window.removeEventListener('popstate', this.onPopState)
    window.removeEventListener('keydown', this.onKeyDown)

    if (this.requestController) {
      this.requestController.abort()
    }
  },
  methods: {
    getCollectionRoot () {
      return this.$el
    },

    getGridElement () {
      return this.getCollectionRoot().querySelector('#productGrid')
    },

    getFilterForm () {
      return this.getCollectionRoot().querySelector('[data-collection-filter-form]')
    },

    getSortInputs () {
      return this.getCollectionRoot().querySelectorAll('.collection-sort__option-input')
    },

    getDrawerSectionToggles () {
      return this.getCollectionRoot().querySelectorAll('[data-collection-drawer-section-toggle]')
    },

    getApplyButtons () {
      return this.getCollectionRoot().querySelectorAll('[data-collection-drawer-apply]')
    },

    getDrawerActiveFiltersContainer () {
      return this.getCollectionRoot().querySelector('[data-collection-drawer-active-filters]')
    },

    getDrawerActiveFiltersList () {
      return this.getCollectionRoot().querySelector('[data-collection-drawer-active-filters-list]')
    },

    getDefaultSortValue () {
      const sortRoot = this.getCollectionRoot().querySelector('[data-collection-sort]')

      return sortRoot ? sortRoot.dataset.defaultSort : ''
    },

    hasStatefulQueryParams () {
      return Object.keys(Shopify.queryParams).some((key) => !['view', 'section_id'].includes(key))
    },

    getDefaultView (breakpoint) {
      const switcher = this.getCollectionRoot().querySelector(`.collection__view-switcher--${breakpoint}`)
      const fallback = VIEW_OPTIONS[breakpoint][0]

      if (!switcher) return fallback

      const defaultView = switcher.dataset.defaultView

      return VIEW_OPTIONS[breakpoint].includes(defaultView) ? defaultView : fallback
    },

    getStoredView (breakpoint) {
      const storedView = localStorage.getItem(VIEW_STORAGE_KEYS[breakpoint])

      return VIEW_OPTIONS[breakpoint].includes(storedView) ? storedView : this.getDefaultView(breakpoint)
    },

    setStoredView (breakpoint, value) {
      if (!VIEW_OPTIONS[breakpoint].includes(value)) return

      localStorage.setItem(VIEW_STORAGE_KEYS[breakpoint], value)
      this.currentViews[breakpoint] = value
      this.applyViewClasses()
      this.updateActiveViewButtons()
    },

    syncViewsFromDom () {
      this.currentViews.desktop = this.getStoredView('desktop')
      this.currentViews.tablet = this.getStoredView('tablet')
      this.currentViews.mobile = this.getStoredView('mobile')

      this.$nextTick(() => {
        this.applyViewClasses()
        this.updateActiveViewButtons()
      })
    },

    applyViewClasses () {
      const grid = this.getGridElement()

      if (!grid) return

      grid.classList.remove(
        'collection__grid--desktop-three',
        'collection__grid--desktop-four',
        'collection__grid--desktop-six',
        'collection__grid--tablet-three',
        'collection__grid--tablet-four',
        'collection__grid--mobile-one',
        'collection__grid--mobile-two'
      )

      grid.classList.add(`collection__grid--desktop-${this.currentViews.desktop}`)
      grid.classList.add(`collection__grid--tablet-${this.currentViews.tablet}`)
      grid.classList.add(`collection__grid--mobile-${this.currentViews.mobile}`)
    },

    updateActiveViewButtons () {
      const root = this.getCollectionRoot()

      Object.keys(VIEW_OPTIONS).forEach((breakpoint) => {
        root.querySelectorAll(`.collection__view-switcher--${breakpoint} .collection__view-option`).forEach((button) => {
          const isActive = button.dataset.viewValue === this.currentViews[breakpoint]

          button.classList.toggle('is-active', isActive)
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        })
      })
    },

    bindDomEvents () {
      const root = this.getCollectionRoot()

      const toolbarTrigger = root.querySelector('.collection__toolbar-trigger')

      if (toolbarTrigger) {
        toolbarTrigger.onclick = () => {
          this.toggleToolbarPanels()
        }
      }

      root.querySelectorAll('[data-collection-drawer-close]').forEach((button) => {
        button.onclick = () => {
          this.closeToolbarPanels()
        }
      })

      root.querySelectorAll('[data-collection-drawer-apply]').forEach((button) => {
        button.onclick = () => {
          this.applyDrawerState()
        }
      })

      root.querySelectorAll('[data-collection-drawer-reset]').forEach((button) => {
        button.onclick = () => {
          this.resetDrawerStateAndApply()
        }
      })

      root.querySelectorAll('[data-collection-drawer-clear-all]').forEach((button) => {
        button.onclick = () => {
          this.clearDrawerFiltersAndApply()
        }
      })

      this.getDrawerSectionToggles().forEach((button) => {
        button.onclick = () => {
          this.toggleDrawerSection(button)
        }
      })

      root.querySelectorAll('.collection__view-option').forEach((button) => {
        button.onclick = (event) => {
          const switcher = event.currentTarget.closest('.collection__view-switcher')

          if (!switcher) return

          this.setStoredView(switcher.dataset.breakpoint, event.currentTarget.dataset.viewValue)
        }
      })

      const filterForm = this.getFilterForm()

      if (filterForm) {
        filterForm.onsubmit = (event) => {
          event.preventDefault()
          this.applyDrawerState()
        }

        filterForm.onchange = () => {
          this.syncDrawerActiveFilters()
          this.syncDrawerSections()
          this.updateApplyButtonState()
        }

        filterForm.oninput = (event) => {
          if (!event.target.matches('.collection-filters__price-input')) return

          this.syncDrawerActiveFilters()
          this.syncDrawerSections()
          this.updateApplyButtonState()
        }
      }

      this.getSortInputs().forEach((input) => {
        input.onchange = (event) => {
          const option = event.currentTarget.closest('.collection-sort__option')

          root.querySelectorAll('.collection-sort__option').forEach((node) => {
            node.classList.toggle('is-active', node === option)
          })

          this.syncDrawerSections()
          this.updateApplyButtonState()
        }
      })

      root.querySelectorAll('.collection-filters__clear, .collection-filters__active-filter, .pagination a').forEach((link) => {
        link.onclick = (event) => {
          const href = event.currentTarget.getAttribute('href')

          if (!href || href === '#') return

          event.preventDefault()
          this.fetchCollectionState(href)
        }
      })

      this.syncActiveSortOptions()
      this.syncDrawerSections()
      this.syncDrawerActiveFilters()
      this.syncToolbarUi()
      this.updateApplyButtonState()
    },

    closeToolbarPanels () {
      this.restoreDrawerSelections()
      this.drawerOpen = false
      this.syncToolbarUi()
    },

    toggleToolbarPanels () {
      this.drawerOpen = !this.drawerOpen
      this.syncToolbarUi()
    },

    syncToolbarUi () {
      const root = this.getCollectionRoot()

      if (!root) return

      const panelsOpen = this.drawerOpen
      const toolbarTrigger = root.querySelector('.collection__toolbar-trigger')
      const drawer = root.querySelector('[data-collection-drawer]')
      const toolbarPanels = root.querySelector('.collection__toolbar-panels')

      if (toolbarTrigger) {
        toolbarTrigger.setAttribute('aria-expanded', panelsOpen ? 'true' : 'false')
      }

      if (drawer) {
        drawer.classList.toggle('is-open', panelsOpen)
      }

      if (toolbarPanels) {
        toolbarPanels.classList.toggle('is-open', panelsOpen)
      }

      document.body.classList.toggle('collection-drawer-open', panelsOpen)
    },

    syncActiveSortOptions () {
      const root = this.getCollectionRoot()

      root.querySelectorAll('.collection-sort__option').forEach((option) => {
        const input = option.querySelector('.collection-sort__option-input')

        option.classList.toggle('is-active', !!input && input.checked)
      })
    },

    getDrawerStateParams () {
      const filterForm = this.getFilterForm()
      const params = new URLSearchParams(window.location.search)
      const defaultSortValue = this.getDefaultSortValue()
      const selectedSort = this.getCollectionRoot().querySelector('.collection-sort__option-input:checked')

      this.clearFilterParams(params)
      params.delete('page')

      if (filterForm) {
        const formData = new FormData(filterForm)

        for (const [key, value] of formData.entries()) {
          if (value !== '') {
            params.append(key, value)
          }
        }
      }

      if (selectedSort && selectedSort.value !== '' && selectedSort.value !== defaultSortValue) {
        params.set('sort_by', selectedSort.value)
      } else {
        params.delete('sort_by')
      }

      return params
    },

    buildCollectionUrlFromParams (params) {
      const queryString = params.toString()

      return queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
    },

    serializeComparableSearchParams (params) {
      const entries = []

      params.forEach((value, key) => {
        if (key === 'section_id') return

        entries.push([key, value])
      })

      entries.sort(([keyA, valueA], [keyB, valueB]) => {
        if (keyA === keyB) {
          return valueA.localeCompare(valueB)
        }

        return keyA.localeCompare(keyB)
      })

      return entries.map(([key, value]) => `${key}=${value}`).join('&')
    },

    resetApplyButtonState () {
      this.applyStateRequestId += 1

      if (this.previewRequestController) {
        this.previewRequestController.abort()
        this.previewRequestController = null
      }

      this.getApplyButtons().forEach((button) => {
        button.classList.remove('is-active')
        button.textContent = button.dataset.defaultLabel || 'Apply'
      })
    },

    setApplyButtonState (count) {
      this.getApplyButtons().forEach((button) => {
        button.classList.add('is-active')
        button.textContent = `${button.dataset.defaultLabel || 'Apply'} (${count})`
      })
    },

    async updateApplyButtonState () {
      const stagedParams = this.getDrawerStateParams()
      const stagedComparableSearch = this.serializeComparableSearchParams(stagedParams)
      const currentComparableSearch = this.serializeComparableSearchParams(new URLSearchParams(window.location.search))

      if (stagedComparableSearch === currentComparableSearch) {
        this.resetApplyButtonState()
        return
      }

      const requestId = ++this.applyStateRequestId

      this.getApplyButtons().forEach((button) => {
        button.classList.add('is-active')
        button.textContent = button.dataset.defaultLabel || 'Apply'
      })

      try {
        const resultsCount = await this.fetchPreviewResultsCount(this.buildCollectionUrlFromParams(stagedParams))

        if (requestId !== this.applyStateRequestId || resultsCount === null) return

        this.setApplyButtonState(resultsCount)
      } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.applyStateRequestId) {
          this.getApplyButtons().forEach((button) => {
            button.classList.add('is-active')
          })
        }
      }
    },

    async fetchPreviewResultsCount (url) {
      const normalizedUrl = new URL(url, window.location.origin)
      const sectionUrl = new URL(normalizedUrl.toString())

      sectionUrl.searchParams.set('section_id', 'product-grid')

      if (this.previewRequestController) {
        this.previewRequestController.abort()
      }

      this.previewRequestController = new AbortController()

      const incomingSection = await this.fetchIncomingSection(sectionUrl, normalizedUrl, this.previewRequestController)

      if (!incomingSection) return null

      const incomingApplyButton = incomingSection.querySelector('[data-collection-drawer-apply]')
      const buttonResultsCount = incomingApplyButton ? parseInt(incomingApplyButton.dataset.resultsCount || '', 10) : NaN

      if (!Number.isNaN(buttonResultsCount)) {
        return buttonResultsCount
      }

      const resultsCountNode = incomingSection.querySelector('[data-collection-results-count]')
      const resultsCount = resultsCountNode ? parseInt(resultsCountNode.textContent.trim(), 10) : NaN

      if (!Number.isNaN(resultsCount)) {
        return resultsCount
      }

      return incomingSection.querySelectorAll('.collection__grid-item:not(.collection__grid-item--description)').length
    },

    toggleDrawerSection (toggle) {
      const panel = toggle.nextElementSibling
      const nextOpenState = toggle.getAttribute('aria-expanded') !== 'true'

      toggle.setAttribute('aria-expanded', nextOpenState ? 'true' : 'false')
      toggle.classList.toggle('is-open', nextOpenState)

      if (panel) {
        panel.classList.toggle('is-open', nextOpenState)
      }
    },

    syncDrawerSections () {
      const root = this.getCollectionRoot()

      root.querySelectorAll('.collection-filters__group').forEach((group) => {
        const toggle = group.querySelector('[data-collection-drawer-section-toggle]')
        const panel = group.querySelector('[data-collection-drawer-section-panel]')
        const hasActiveValues = group.querySelectorAll('.collection-filters__option-input:checked').length > 0 || Array.from(group.querySelectorAll('.collection-filters__price-input')).some((input) => input.value !== '')

        if (!toggle || !panel) return

        if (hasActiveValues) {
          toggle.setAttribute('aria-expanded', 'true')
          toggle.classList.add('is-open')
          panel.classList.add('is-open')
        }
      })
    },

    syncDrawerActiveFilters () {
      const container = this.getDrawerActiveFiltersContainer()
      const list = this.getDrawerActiveFiltersList()
      const filterForm = this.getFilterForm()

      if (!container || !list || !filterForm) return

      const chips = []

      filterForm.querySelectorAll('.collection-filters__option-input:checked').forEach((input) => {
        chips.push({
          label: input.dataset.filterLabel || input.value,
          action: () => {
            input.checked = false
            this.syncDrawerActiveFilters()
          }
        })
      })

      const priceInputs = Array.from(filterForm.querySelectorAll('.collection-filters__price-input'))
      const minPriceInput = priceInputs.find((input) => input.name.includes('gte'))
      const maxPriceInput = priceInputs.find((input) => input.name.includes('lte'))

      if ((minPriceInput && minPriceInput.value !== '') || (maxPriceInput && maxPriceInput.value !== '')) {
        const minValue = minPriceInput && minPriceInput.value !== '' ? minPriceInput.value : '0'
        const maxValue = maxPriceInput && maxPriceInput.value !== '' ? maxPriceInput.value : (maxPriceInput ? maxPriceInput.placeholder : '')

        chips.push({
          label: `Price ${minValue}-${maxValue}`,
          action: () => {
            if (minPriceInput) minPriceInput.value = ''
            if (maxPriceInput) maxPriceInput.value = ''
            this.syncDrawerActiveFilters()
          }
        })
      }

      list.innerHTML = ''

      chips.forEach((chip, index) => {
        const button = document.createElement('button')

        button.type = 'button'
        button.className = 'collection__drawer-active-chip'
        button.dataset.chipIndex = `${index}`
        button.innerHTML = `<span>${chip.label}</span><span aria-hidden="true">&times;</span>`
        button.onclick = () => {
          chip.action()
          this.syncDrawerSections()
          this.updateApplyButtonState()
        }

        list.appendChild(button)
      })

      container.hidden = chips.length === 0
    },

    restoreDrawerSelections () {
      const filterForm = this.getFilterForm()

      if (filterForm) {
        filterForm.reset()
      }

      this.getSortInputs().forEach((input) => {
        input.checked = input.defaultChecked
      })

      this.syncActiveSortOptions()
      this.syncDrawerActiveFilters()
      this.updateApplyButtonState()
    },

    resetDrawerSelections () {
      const filterForm = this.getFilterForm()
      const defaultSortValue = this.getDefaultSortValue()

      if (filterForm) {
        filterForm.reset()

        filterForm.querySelectorAll('.collection-filters__option-input').forEach((input) => {
          input.checked = false
        })

        filterForm.querySelectorAll('.collection-filters__price-input').forEach((input) => {
          input.value = ''
        })
      }

      this.getSortInputs().forEach((input, index) => {
        input.checked = defaultSortValue ? input.value === defaultSortValue : index === 0
      })

      this.syncActiveSortOptions()
      this.syncDrawerActiveFilters()
      this.updateApplyButtonState()
    },

    resetDrawerStateAndApply () {
      const params = new URLSearchParams(window.location.search)

      this.clearFilterParams(params)
      params.delete('sort_by')
      params.delete('page')

      const queryString = params.toString()
      const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname

      this.fetchCollectionState(nextUrl)
    },

    clearDrawerFiltersAndApply () {
      const filterForm = this.getFilterForm()

      if (filterForm) {
        filterForm.querySelectorAll('.collection-filters__option-input').forEach((input) => {
          input.checked = false
        })

        filterForm.querySelectorAll('.collection-filters__price-input').forEach((input) => {
          input.value = ''
        })
      }

      this.syncDrawerActiveFilters()
      this.syncDrawerSections()
      this.applyDrawerState()
    },

    applyDrawerState () {
      const nextUrl = this.buildCollectionUrlFromParams(this.getDrawerStateParams())

      this.fetchCollectionState(nextUrl)
    },

    clearFilterParams (params) {
      const filterKeys = []

      params.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'page') {
          filterKeys.push(key)
        }
      })

      filterKeys.forEach((key) => {
        params.delete(key)
      })
    },

    async fetchCollectionState (url, options = {}) {
      const normalizedUrl = new URL(url, window.location.origin)
      const sectionUrl = new URL(normalizedUrl.toString())

      sectionUrl.searchParams.set('section_id', 'product-grid')

      if (this.requestController) {
        this.requestController.abort()
      }

      this.requestController = new AbortController()
      this.isLoading = true

      try {
        const incomingSection = await this.fetchIncomingSection(sectionUrl, normalizedUrl, this.requestController)

        if (!incomingSection) return

        this.replaceCollectionDom(incomingSection)
        this.drawerOpen = false

        const historyQuery = this.serializeSearchParams(normalizedUrl.searchParams)
        const historyUrl = `${normalizedUrl.pathname}${historyQuery ? `?${historyQuery}` : ''}`

        if (!options.skipHistory) {
          window.history.pushState({ path: historyUrl }, '', historyUrl)
        }

        this.syncQueryParamsFromUrl()
        this.$nextTick(() => {
          this.bindDomEvents()
          this.applyViewClasses()
          this.updateActiveViewButtons()
          this.scrollToProductGrid()
          initRedesignCardSliders(this.$el)
        })
      } catch (error) {
        if (error.name !== 'AbortError') {
          window.location.assign(url)
        }
      } finally {
        this.isLoading = false
      }
    },

    async fetchIncomingSection (sectionUrl, fallbackUrl, controller) {
      const parser = new window.DOMParser()
      const requestOptions = {
        signal: controller.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      }

      const sectionResponse = await window.fetch(sectionUrl.toString(), requestOptions)

      if (sectionResponse.ok) {
        const sectionHtml = await sectionResponse.text()
        const sectionDocument = parser.parseFromString(sectionHtml, 'text/html')
        const incomingSection = sectionDocument.querySelector('[data-module="collection"]')

        if (incomingSection) {
          return incomingSection
        }
      }

      const fullResponse = await window.fetch(fallbackUrl.toString(), requestOptions)

      if (!fullResponse.ok) {
        throw new Error(`Collection fetch failed with status ${fullResponse.status}`)
      }

      const fullHtml = await fullResponse.text()
      const fullDocument = parser.parseFromString(fullHtml, 'text/html')

      return fullDocument.querySelector('[data-module="collection"]')
    },

    replaceCollectionDom (incomingSection) {
      const currentRoot = this.getCollectionRoot()
      const currentToolbar = currentRoot.querySelector('[data-collection-toolbar]')
      const currentDrawer = currentRoot.querySelector('[data-collection-drawer]')
      const currentResults = currentRoot.querySelector('[data-collection-results]')
      const currentPagination = currentRoot.querySelector('[data-collection-pagination]')

      const incomingToolbar = incomingSection.querySelector('[data-collection-toolbar]')
      const incomingDrawer = incomingSection.querySelector('[data-collection-drawer]')
      const incomingResults = incomingSection.querySelector('[data-collection-results]')
      const incomingPagination = incomingSection.querySelector('[data-collection-pagination]')

      if (currentToolbar && incomingToolbar) currentToolbar.replaceWith(incomingToolbar)
      if (currentDrawer && incomingDrawer) currentDrawer.replaceWith(incomingDrawer)
      if (currentResults && incomingResults) currentResults.replaceWith(incomingResults)
      if (currentPagination && incomingPagination) currentPagination.replaceWith(incomingPagination)
    },

    serializeSearchParams (params) {
      const filteredParams = new URLSearchParams(params)

      filteredParams.delete('section_id')

      return filteredParams.toString()
    },

    syncQueryParamsFromUrl () {
      Shopify.queryParams = {}

      const params = new URLSearchParams(window.location.search)
      params.forEach((value, key) => {
        Shopify.queryParams[key] = value
      })
    },

    scrollToProductGrid () {
      this.$nextTick(() => {
        const productGrid = this.getGridElement()

        if (productGrid) {
          window.scrollTo({
            top: productGrid.getBoundingClientRect().top + window.scrollY - 260,
            behavior: 'smooth'
          })
        }
      })
    },

    handlePopState () {
      this.fetchCollectionState(window.location.href, { skipHistory: true })
    },

    handleKeyDown (event) {
      if (event.key === 'Escape' && this.drawerOpen) {
        this.closeToolbarPanels()
      }
    }
  }
}))
